import FormControl from '@mui/material/FormControl';
import type { InputProps as MuiInputProps } from '@mui/material/Input';
import Input from '@mui/material/Input';
import type { Currency } from '@ps/web-core-enums';
import type { TrackedTranslation, Values } from '@ps/web-core-intl';
import { useTrackers } from '@ps/web-shared-analytics';
import {
  InfoTooltip,
  Translation,
  useErrorLabels,
  useTrackingLabel
} from '@ps/web-shared-components';
import type { InjectedValidatorProps, InputProps } from '@ps/web-shared-components';
import { Label } from '@ps/web-shared-ui';
import classNames from 'clsx';
import React, { useCallback, useMemo } from 'react';
import { Field } from 'react-final-form';
import { FormattedNumberParts, useIntl } from 'react-intl';

import type { FieldNameProp } from '@swf/core/types/Props';

import { zeroExponentCurrencies } from '../../utils/cashier';

import { TXTCLI_Amount } from './AmountInput.messages.json';
import {
  formControl,
  formControlError,
  formControlExtension,
  inputDisabled,
  root
} from './AmountInput.scss';

export interface Props
  extends Pick<InputProps, 'labelClassName' | 'tooltip' | 'tooltipHover'>,
    MuiInputProps,
    FieldNameProp,
    InjectedValidatorProps {
  currency: Currency;
  hasExtension: boolean;
  hideLabel?: boolean;
  label?: TrackedTranslation;
  maxInputAmount?: number;
  normalize?: boolean;
  onAmountBlur?: () => void;
  onFocus?: () => void;
  validateFields?: string[];
  values?: Values;
  withPlaceholder?: boolean;
}

export const normalizeOnBlur = (value: unknown, onChange: (val: string) => void) => {
  const num = +`${value}`.replace(/,/g, '');
  if (!isNaN(num)) onChange(num.toFixed(2));
};

const AmountInput: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  currency,
  fieldName,
  hasExtension,
  hideLabel,
  label = TXTCLI_Amount,
  labelClassName,
  maxInputAmount,
  onAmountBlur,
  onFocus,
  normalize = true,
  tooltip,
  tooltipHover,
  validateFields,
  validationErrors,
  validator,
  values,
  withPlaceholder = true,
  disabled = false,
  ...inputProps
}) => {
  const { formatNumber, formatNumberToParts } = useIntl();
  const { inputFilled, inputFocused } = useTrackers();
  const trackingFieldName = useTrackingLabel(label, values);
  const fieldErrors = useErrorLabels(validationErrors);
  const fieldType = 'number';
  const isZeroExponentCurrency = zeroExponentCurrencies.includes(currency);

  // Extract decimal character from localized string "99,999.99" or "99 999,99" etc.
  const decimal = useMemo(
    () =>
      formatNumberToParts(99999.99, {
        currency,
        style: 'currency',
        minimumFractionDigits: 2
      }).find(({ type }) => 'decimal' === type)?.value || (9.99).toLocaleString().charAt(1),
    [currency, formatNumberToParts]
  );

  const regDecimal = useMemo(() => new RegExp(`(?=\\${decimal})`), [decimal]);

  const leadingDecimal = useMemo(() => new RegExp(`(^\\${decimal})`), [decimal]);

  const trimCurrencyCode = useCallback(
    (num: number, cur: Currency, maximumFractionDigits = 2, minimumFractionDigits?: number) => {
      const regex = new RegExp(`\\s?${cur}\\s?`);

      return formatNumber(num, {
        currency: cur,
        style: 'currency',
        currencyDisplay: 'code',
        minimumFractionDigits,
        maximumFractionDigits
      }).replace(regex, '');
    },
    [formatNumber]
  );

  // Field value
  const parse = useCallback(
    // value is any by Final Form types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any) => {
      if (!value) {
        return value;
      }

      const [dollars, cents] = value
        // Allow user to type . or , as decimal separator
        .replace(/([,.]$)/, decimal)
        .replace(leadingDecimal, `0${decimal}`)
        .split(regDecimal);

      const whole = dollars.replace(/\D/g, '');
      if (isZeroExponentCurrency) {
        return whole;
      }
      const fraction = cents ? cents.substring(0, 3).replace(decimal, '.') : '';

      const formattedValue = whole + fraction.replace(/[^\d,.]/g, '');

      return maxInputAmount && +formattedValue > maxInputAmount
        ? maxInputAmount.toString()
        : formattedValue;
    },
    [decimal, leadingDecimal, regDecimal, isZeroExponentCurrency, maxInputAmount]
  );

  // Presentation
  const format = useCallback(
    (value: string | number = '') => {
      if (value) {
        const decimalSeparator = value
          .toLocaleString('decimal', { useGrouping: false })
          .includes('.')
          ? decimal
          : '';
        const [dollars, cents = ''] = value
          .toLocaleString('decimal', { useGrouping: false })
          .split('.');
        return trimCurrencyCode(+dollars, currency, 0, 0) + decimalSeparator + cents;
      }

      return '';
    },
    [currency, decimal, trimCurrencyCode]
  );

  return (
    <Field
      format={format}
      name={fieldName}
      parse={parse}
      validate={validator}
      validateFields={validateFields}
    >
      {({ input: { onChange, onBlur, value }, meta: { dirty, touched, invalid } }) => (
        <FormControl classes={{ root }} error={dirty && touched && invalid}>
          <Label id={fieldName} hidden={hideLabel} className={labelClassName}>
            <Translation {...label} values={values} />
            {tooltip && <InfoTooltip content={tooltip} addHoverEffect={tooltipHover} />}
          </Label>
          <FormattedNumberParts value={0} style="currency" currency={currency}>
            {() => (
              <Input
                {...inputProps}
                disabled={disabled}
                disableUnderline
                type="tel"
                name={fieldName}
                id={fieldName}
                value={value}
                onChange={onChange}
                onBlur={(event) => {
                  normalize && normalizeOnBlur(value, onChange);
                  normalize && Number(currency).toFixed(2);
                  onAmountBlur?.();
                  onBlur?.(event);
                  value &&
                    inputFilled({
                      fieldName: trackingFieldName,
                      fieldType,
                      fieldErrors,
                      numInput: +parse(value)
                    });
                }}
                onFocus={() => {
                  onFocus?.();
                  inputFocused({ fieldName: trackingFieldName, fieldType });
                }}
                placeholder={withPlaceholder ? trimCurrencyCode(0, currency) : ''}
                autoComplete="off"
                error={touched && invalid}
                classes={{
                  root,
                  disabled: inputDisabled,
                  formControl: classNames(formControl, {
                    [formControlExtension]: hasExtension
                  }),
                  error: formControlError,
                  ...inputProps.classes
                }}
              />
            )}
          </FormattedNumberParts>
        </FormControl>
      )}
    </Field>
  );
};

export default AmountInput;
